import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async'; // ✅ SEO Import
import LiteYouTubeEmbed from 'react-lite-youtube-embed';
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css';

import '../Styles/Home.css';
import HomeServices from './HomeServices';
import HomeTeam from './HomeTeam';
import Testimonials from './Testimonials';

// --- Define the hook OUTSIDE the Home component ---
const useIntersectionObserver = (options) => {
  const [entry, setEntry] = useState(null);
  const [node, setNode] = useState(null);

  const observer = useRef(null);

  useEffect(() => {
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(([entry]) => {

      if (entry.isIntersecting) {
        setEntry(entry);

        if (observer.current) observer.current.disconnect();
      }
    }, options);

    const { current: currentObserver } = observer;
    if (node) currentObserver.observe(node);

    return () => currentObserver.disconnect();
  }, [node, options]);

  return [setNode, entry];
};

// --- Define the helper component OUTSIDE the Home component ---
const AnimateOnScroll = ({ children, animationClass }) => {
  const [ref, entry] = useIntersectionObserver({ threshold: 0.1 });

  return (

    <div ref={ref} className={`scroll-animate ${entry?.isIntersecting ? animationClass : ''}`}>
      {children}
    </div>
  );
};

const Home = () => {

  return (
    <HelmetProvider>
      {/* ✅ SEO + Social Meta Tags */}
      <Helmet>
        <title>Groomwell Women Salon Hafeezpet | Best Hair & Beauty Services</title>
        <meta
          name="description"
          content="Groomwell Salon in Hafeezpet offers premium women's haircuts, spa, bridal makeup, facials & skincare. Rated 4.9★ – trusted salon near you."
        />
        <meta
          name="keywords"
          content="women salon Hafeezpet, best salon Hafeezpet, Groomwell Salon Hafeezpet, ladies beauty parlour Hafeezpet, bridal makeup Hafeezpet, hair spa Hafeezpet, facials Hafeezpet"
        />
        <meta name="author" content="Groomwell Salon Hafeezpet" />

        {/* ✅ Open Graph tags */}
        <meta property="og:title" content="Groomwell Salon Hafeezpet - Best Women Salon & Bridal Makeup" />
        <meta property="og:description" content="Visit Groomwell Salon Hafeezpet for hair spa, facials, bridal makeup & more. Trusted 4.9★ women’s salon." />
        <meta property="og:image" content="https://www.groomwellsalon.in/GROOMWELLINTERIOR.jpg" />
        <meta property="og:url" content="https://www.groomwellsalon.in/" />
        <meta property="og:type" content="website" />

        {/* ✅ Instagram Profile */}
        <link rel="me" href="https://www.instagram.com/groomwellsalon/" /> 

        {/* ✅ LocalBusiness Structured Data */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "BeautySalon",
              "name": "Groomwell Salon Hafeezpet",
              "image": "https://www.groomwellsalon.in/GROOMWELLINTERIOR.jpg",
              "url": "https://www.groomwellsalon.in/",
              "telephone": "+91-6304835560",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "115/99(P)87, Manjeera Pipeline Rd, above SBI Bank, Vinayaka Nagar, Hafeezpet",
                "addressLocality": "Hyderabad",
                "addressRegion": "Telangana",
                "postalCode": "500049",
                "addressCountry": "IN"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": 17.485240,
                "longitude": 78.357501   
              },
              "openingHours": "Mo-Su 09:00-21:00",
              "priceRange": "₹₹",
              "sameAs": [
                "https://www.instagram.com/groomwellsalon/"
              ]
            }
          `}
        </script>
      </Helmet>

      <div>
        <div className="hero-section">
          <AnimateOnScroll animationClass="slide-in-left">
            <h1>GLAMOUR & STYLE</h1>
            <h1>FOR EVERY WOMAN</h1>
            <p>Step into Groomwell Salon – where beauty meets confidence.</p>
            <p>Trusted 4.9★ Women’s Salon in Hafeezpet, Hyderabad for Hair, Makeup & Skincare.</p>
            <button className="hero-button">
              <a href="/Services" className="hero-button-link">OUR SERVICES</a>
            </button>
          </AnimateOnScroll>
        </div>

        <div className="home-about-section">
          <AnimateOnScroll animationClass="slide-in-left">
            <div className="home-about-left">
              <h1 className="home-about-title">WELCOME TO <br />GROOMWELL SALON</h1>
              <div className="home-about-title-underline"></div>
            </div>
          </AnimateOnScroll>

          <div className="home-about-right">
            <AnimateOnScroll animationClass="slide-in-right">
              <p className="home-about-description">
                At GROOMWELL Salon Hafeezpet, we believe every woman deserves to look and feel her best. 
                With 18+ years of professional experience and a team of expert hair stylists, beauticians, 
                and makeup artists, we provide premium women’s salon services in Hafeezpet, Hyderabad.
                <br /><br />
                From luxurious hair spa treatments and professional hair colouring to bridal makeup, facials, 
                waxing, and skin care, we offer everything under one roof. Our salon is trusted by hundreds of 
                happy clients, earning a 4.9★ Google rating for consistent quality and personalized care.
                <br /><br />
                Whether you’re preparing for your big day, refreshing your everyday style, or indulging in a 
                relaxing spa, GROOMWELL Salon Hafeezpet is your go-to destination for beauty and confidence.
              </p>
              <button className="home-about-read-more">
                <a href="/Aboutpage" className="hero-button-link">— READ MORE</a>
              </button>
            </AnimateOnScroll>
          </div>
        </div>

        <AnimateOnScroll animationClass="pop-up">
        <div className="home-about-image-container">

          {/*
          <iframe
            src="https://www.youtube.com/embed/JOVrJrIT_wk"
            title="Groomwell Salon Hafeezpet Video Tour"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="home-about-image"
          ></iframe>
          */}
          <div className="home-about-play-icon">
          <LiteYouTubeEmbed
            id="JOVrJrIT_wk" // The YouTube video ID
            title="Groomwell Salon Hafeezpet Video Tour"
            wrapperClass="home-about-image" // Apply your existing class for sizing
          /></div>
        </div>
      </AnimateOnScroll>

        <HomeServices />
        <HomeTeam />
        <Testimonials />
      </div>
    </HelmetProvider>
  );
};

export default Home;
